import React from 'react';
import PropTypes from 'prop-types';
import { DataTable, CheckboxFilter } from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';
import TableTextFilter from './TableTextFilter';
import CustomDataTableEmptyState from './CustomDataTableEmptyState';
import RequestDetailsTableCell from './RequestDetailsTableCell';
import RequestStatusTableCell from './RequestStatusTableCell';
import RequestAmountTableCell from './RequestAmountTableCell';
import RequestRecentActionTableCell from './RequestRecentActionTableCell';
import ApprovedRequestActionsTableCell from './ApprovedRequestActionsTableCell';
import ApprovedRequestsTableRefreshAction from './ApprovedRequestsTableRefreshAction';
import ApprovedRequestBulkRemindAction from './ApprovedRequestBulkRemindAction';
import ApprovedRequestBulkCancelAction from './ApprovedRequestBulkCancelAction';
import { DEFAULT_PAGE, PAGE_SIZE } from './data';
import { transformLearnerRequestStateCounts } from './data/utils';

const FilterStatus = (rest) => (
  <DataTable.FilterStatus showFilteredFields={false} {...rest} />
);

const selectColumn = {
  id: 'selection',
  Header: DataTable.ControlledSelectHeader,
  Cell: DataTable.ControlledSelect,
  disableSortBy: true,
};

const BudgetDetailApprovedRequestTable = ({
  isLoading,
  tableData,
  fetchTableData,
  isRetiredOrExpired,
}) => {
  const intl = useIntl();
  const statusFilterChoices = transformLearnerRequestStateCounts(tableData.requestStatusCounts);

  const approvedRequestsTableData = (() => ({
    tableActions: [
      <ApprovedRequestsTableRefreshAction refresh={fetchTableData} />,
    ],
    additionalColumns: [],
    bulkActions: !isRetiredOrExpired ? [
      <ApprovedRequestBulkRemindAction learnerRequestStateCounts={tableData.requestStatusCounts} />,
      <ApprovedRequestBulkCancelAction
        learnerRequestStateCounts={tableData.requestStatusCounts}
      />,
    ] : [],
  }))();

  return (
    <DataTable
      isSortable
      manualSortBy
      isPaginated
      manualPagination
      isFilterable
      manualFilters
      isLoading={isLoading}
      defaultColumnValues={{ Filter: TableTextFilter }}
      manualSelectColumn={selectColumn}
      FilterStatusComponent={FilterStatus}
      SelectionStatusComponent={DataTable.ControlledSelectionStatus}
      columns={[
        {
          Header: intl.formatMessage({
            id: 'lcm.budget.detail.page.approved.requests.table.columns.request.details',
            defaultMessage: 'Request details',
            description:
              'Column header for the request details column in the approved requests table',
          }),
          accessor: 'requestDetails',
          Cell: RequestDetailsTableCell,
          disableSortBy: true,
        },
        {
          Header: intl.formatMessage({
            id: 'lcm.budget.detail.page.approved.requests.table.columns.amount',
            defaultMessage: 'Amount',
            description:
              'Column header for the amount column in the approved requests table',
          }),
          accessor: 'amount',
          Cell: RequestAmountTableCell,
          disableFilters: true,
        },
        {
          Header: intl.formatMessage({
            id: 'lcm.budget.detail.page.approved.requests.table.columns.status',
            defaultMessage: 'Status',
            description:
              'Column header for the status column in the approved requests table',
          }),
          accessor: 'learnerRequestState',
          Cell: RequestStatusTableCell,
          Filter: CheckboxFilter,
          filter: 'includesValue',
          filterChoices: statusFilterChoices,
          disableFilters: false,
        },
        {
          Header: intl.formatMessage({
            id: 'lcm.budget.detail.page.approved.requests.table.columns.recent.action',
            defaultMessage: 'Recent action',
            description:
              'Column header for the recent action column in the approved requests table',
          }),
          accessor: 'recentAction',
          Cell: RequestRecentActionTableCell,
          disableFilters: true,
        },
        {
          Header: intl.formatMessage({
            id: 'lcm.budget.detail.page.approved.requests.table.columns.actions',
            defaultMessage: 'Actions',
            description:
              'Column header for the actions column in the approved requests table',
          }),
          accessor: 'actions',
          Cell: ApprovedRequestActionsTableCell,
          disableFilters: true,
          disableSortBy: true,
        },
      ]}
      additionalColumns={approvedRequestsTableData.additionalColumns}
      tableActions={approvedRequestsTableData.tableActions}
      initialTableOptions={{
        getRowId: (row) => row?.uuid?.toString(),
      }}
      initialState={{
        pageSize: PAGE_SIZE,
        pageIndex: DEFAULT_PAGE,
        sortBy: [
          {
            id: 'recentAction',
            desc: true,
          },
        ],
        filters: [],
      }}
      fetchData={fetchTableData}
      data={tableData.results || []}
      itemCount={tableData.count || 0}
      pageCount={tableData.numPages || 1}
      EmptyTableComponent={CustomDataTableEmptyState}
      isSelectable={!isRetiredOrExpired}
      bulkActions={approvedRequestsTableData.bulkActions}
    />
  );
};

BudgetDetailApprovedRequestTable.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  tableData: PropTypes.shape({
    results: PropTypes.arrayOf(PropTypes.shape()),
    requestStatusCounts: PropTypes.arrayOf(
      PropTypes.shape({
        learnerRequestState: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
      }),
    ),
    count: PropTypes.number.isRequired,
    numPages: PropTypes.number.isRequired,
  }).isRequired,
  fetchTableData: PropTypes.func.isRequired,
  isRetiredOrExpired: PropTypes.bool,
};

BudgetDetailApprovedRequestTable.defaultProps = {
  isRetiredOrExpired: false,
};

export default BudgetDetailApprovedRequestTable;
